import React, { useState, useEffect } from 'react';
import { microsoftService, OneDriveItem } from '../../../../../services/microsoftService';
import { ExcelWidgetConfig, ExcelDataContent } from './types';
import { DashboardDataContent } from '../../../blocks/types';
import { WidgetLoadingState } from '../../../design';
import ExcelInlineViewer from './ExcelInlineViewer';
import { getErrorMessage, getErrorStatus } from '../../../../../utils/errors';

interface ExcelWidgetRendererProps {
  config: ExcelWidgetConfig;
  onFileSelect?: (file: OneDriveItem) => void;
  readOnly?: boolean;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: DashboardDataContent) => void;
}

const ExcelWidgetRenderer: React.FC<ExcelWidgetRendererProps> = ({
  config,
  onFileSelect,
  readOnly = false,
  dashboardData = {},
  onDataChange,
}) => {
  // Check for a persisted file selection in dashboardData
  const persistedFile = config.dataKey
    ? (dashboardData[config.dataKey] as ExcelDataContent | undefined)
    : undefined;

  // Effective file config: persisted selection takes priority over static config
  const effectiveFileId = persistedFile?.fileId || config.fileId;
  const effectiveFileName = persistedFile?.fileName || config.fileName;
  const effectiveFileUrl = persistedFile?.fileUrl || config.fileUrl;
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<OneDriveItem[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [folderStack, setFolderStack] = useState<Array<{ id: string | null; name: string }>>([
    { id: null, name: 'My Files' },
  ]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<OneDriveItem | null>(null);

  // Excel file extensions
  const EXCEL_EXTENSIONS = ['xlsx', 'xls', 'xlsm', 'xlsb', 'csv'];

  // Check Microsoft connection status
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const status = await microsoftService.getConnectionStatus();
        setIsConnected(status.connected);
        // Only load file browser if no file is configured and no persisted selection
        if (status.connected && !effectiveFileId && !effectiveFileUrl) {
          loadFiles(currentFolder);
        }
      } catch (err) {
        setError('Failed to check Microsoft connection');
      } finally {
        setIsLoading(false);
      }
    };
    checkConnection();
  }, []);

  const loadFiles = async (folderId: string | null) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await microsoftService.listFiles(folderId || undefined);
      // Filter to only show folders and Excel files
      const items = response.items.filter((item: OneDriveItem) => {
        if (item.isFolder) return true;
        const ext = item.name.split('.').pop()?.toLowerCase();
        return ext && EXCEL_EXTENSIONS.includes(ext);
      });
      setFiles(items);
    } catch (err: unknown) {
      if (getErrorStatus(err) === 401) {
        setIsConnected(false);
        setError('Microsoft connection expired. Please reconnect.');
      } else {
        setError('Failed to load files');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadFiles(currentFolder);
      return;
    }

    setIsLoading(true);
    try {
      // Search for Excel files
      const response = await microsoftService.searchFiles(`${searchQuery} .xlsx OR .xls`);
      const items = response.items.filter((item: OneDriveItem) => {
        if (item.isFolder) return false;
        const ext = item.name.split('.').pop()?.toLowerCase();
        return ext && EXCEL_EXTENSIONS.includes(ext);
      });
      setFiles(items);
    } catch (err) {
      setError('Search failed');
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToFolder = (folder: OneDriveItem) => {
    setCurrentFolder(folder.id);
    setFolderStack([...folderStack, { id: folder.id, name: folder.name }]);
    loadFiles(folder.id);
  };

  const navigateBack = (index: number) => {
    const newStack = folderStack.slice(0, index + 1);
    const targetFolder = newStack[newStack.length - 1];
    setFolderStack(newStack);
    setCurrentFolder(targetFolder.id);
    loadFiles(targetFolder.id);
  };

  const handleFileClick = (file: OneDriveItem) => {
    if (file.isFolder) {
      navigateToFolder(file);
    } else {
      setSelectedFile(file);
      onFileSelect?.(file);

      // Persist the file selection to dashboardData so it's remembered
      if (config.dataKey && onDataChange) {
        const fileData: ExcelDataContent = {
          fileId: file.id,
          fileName: file.name,
          fileUrl: file.webUrl,
        };
        onDataChange(config.dataKey, fileData as unknown as DashboardDataContent);
      }
    }
  };

  const openExcelFile = async (fileId: string, openMode: 'web' | 'desktop' | 'embed' = config.openIn || 'web') => {
    try {
      setError(null);
      const { downloadUrl, webUrl } = await microsoftService.getDownloadUrl(fileId);

      if (openMode === 'desktop') {
        // Use protocol handler to open in desktop Excel
        // The downloadUrl is a direct file URL that Excel can open
        if (downloadUrl) {
          microsoftService.openExcelProtocol(downloadUrl);
        } else if (webUrl) {
          // Fallback to webUrl if downloadUrl not available
          microsoftService.openExcelProtocol(webUrl);
        } else {
          setError('No download URL available');
        }
      } else {
        // Open in Excel Online
        if (webUrl) {
          const newWindow = window.open(webUrl, '_blank');
          if (!newWindow) {
            setError('Popup blocked - please allow popups for this site');
          }
        } else {
          setError('No web URL available for this file');
        }
      }
    } catch (err: unknown) {
      console.error('Failed to open Excel file:', err);
      // Check if it's a Microsoft auth error
      if (getErrorStatus(err) === 401) {
        setError('Microsoft session expired. Please reconnect your Microsoft account in Settings.');
        setIsConnected(false);
      } else {
        setError(getErrorMessage(err, 'Failed to open Excel file'));
      }
    }
  };


  const connectMicrosoft = async () => {
    try {
      const { authUrl } = await microsoftService.getAuthUrl();
      sessionStorage.setItem('microsoft_auth_return', window.location.href);
      window.open(authUrl, '_blank');
    } catch (err) {
      setError('Failed to start Microsoft authentication');
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper to clear persisted file selection and go back to browser
  const clearFileSelection = () => {
    if (config.dataKey && onDataChange) {
      onDataChange(config.dataKey, {} as unknown as DashboardDataContent);
    }
  };

  // Inline display: parse and render the workbook in-app (sheet-tabbed grid).
  // Only the direct-URL path is supported (no Microsoft Graph fetch needed).
  if (config.displayInline && effectiveFileUrl) {
    return <ExcelInlineViewer fileUrl={effectiveFileUrl} fileName={effectiveFileName} />;
  }

  // If we have a specific file configured (static or persisted), show the quick open view
  if (effectiveFileId || effectiveFileUrl) {
    const isPersistedSelection = !!persistedFile?.fileId;

    return (
      <div className="h-full flex flex-col items-center justify-center p-6 bg-surface">
        <div className="text-center max-w-md">
          {/* Excel Icon */}
          <div className="w-20 h-20 mx-auto mb-4 bg-success-100 rounded-2xl flex items-center justify-center">
            <svg className="w-12 h-12 text-success-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zM8 11h2v2H8v-2zm0 3h2v2H8v-2zm0 3h2v2H8v-2zm3-6h5v2h-5v-2zm0 3h5v2h-5v-2zm0 3h5v2h-5v-2z"/>
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            {effectiveFileName || 'Excel Spreadsheet'}
          </h3>

          <p className="text-neutral-600 mb-4">
            Choose how you want to open this spreadsheet
          </p>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
              {error}
              {error.includes('reconnect') && (
                <a href="/settings" className="block mt-2 text-danger-800 underline font-medium">
                  Go to Settings to reconnect
                </a>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3">
            {/* Open in Excel Online */}
            <button
              onClick={() => {
                if (effectiveFileId) {
                  openExcelFile(effectiveFileId, 'web');
                } else if (effectiveFileUrl) {
                  window.open(effectiveFileUrl, '_blank');
                }
              }}
              className="px-6 py-3 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              Open in Excel Online
            </button>

            {/* Open in Desktop Excel */}
            <button
              onClick={() => {
                if (effectiveFileId) {
                  openExcelFile(effectiveFileId, 'desktop');
                } else if (effectiveFileUrl) {
                  microsoftService.openExcelProtocol(effectiveFileUrl);
                }
              }}
              className="px-6 py-3 border-2 border-success-600 text-success-600 rounded-lg hover:bg-success-50 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Open in Desktop Excel
            </button>

            {/* Change file — only for user-selected files, not statically configured ones */}
            {isPersistedSelection && !readOnly && (
              <button
                onClick={clearFileSelection}
                className="px-4 py-2 text-neutral-500 text-sm hover:text-neutral-700 transition-colors"
              >
                Change file
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Not connected state
  if (!isConnected && !isLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 bg-neutral-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-success-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-success-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13z"/>
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">Connect to Microsoft</h3>
          <p className="text-neutral-600 mb-4">
            Sign in with your Microsoft account to browse and open Excel files from OneDrive.
          </p>
          <button
            onClick={connectMicrosoft}
            className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors flex items-center gap-2 mx-auto"
          >
            <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
              <rect width="10" height="10" fill="#f25022" />
              <rect x="11" width="10" height="10" fill="#7fba00" />
              <rect y="11" width="10" height="10" fill="#00a4ef" />
              <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading && files.length === 0) {
    return <WidgetLoadingState />;
  }

  // Browse mode - show Excel files from OneDrive
  return (
    <div className="h-full flex flex-col bg-surface">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-success-50">
        <h3 className="font-medium text-success-800 flex items-center gap-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13z"/>
          </svg>
          Browse Excel Files
        </h3>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-2 border-b bg-neutral-50">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto">
          {folderStack.map((folder, index) => (
            <React.Fragment key={folder.id || 'root'}>
              {index > 0 && <span className="text-neutral-400">/</span>}
              <button
                onClick={() => navigateBack(index)}
                className={`text-sm hover:text-success-600 whitespace-nowrap ${
                  index === folderStack.length - 1 ? 'font-medium text-neutral-900' : 'text-neutral-600'
                }`}
              >
                {folder.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Refresh */}
        <button
          onClick={() => loadFiles(currentFolder)}
          className="p-1.5 text-neutral-500 hover:text-success-600"
          disabled={isLoading}
          aria-label="Refresh"
        >
          <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Search */}
      <div className="p-2 border-b">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search Excel files..."
            aria-label="Search Excel files"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 px-3 py-1.5 text-sm border rounded"
          />
          <button
            onClick={handleSearch}
            className="px-3 py-1.5 bg-success-600 text-white text-sm rounded hover:bg-success-700"
          >
            Search
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-2 bg-danger-50 text-danger-600 text-sm border-b">
          {error}
        </div>
      )}

      {/* File list */}
      <div className="flex-1 overflow-auto p-2">
        {files.length === 0 ? (
          <div className="text-center py-8 text-neutral-500">
            <svg className="w-12 h-12 mx-auto mb-2 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            No Excel files found
          </div>
        ) : (
          <div className="space-y-1">
            {files.map((file) => (
              <div
                key={file.id}
                role="button"
                tabIndex={0}
                onClick={() => handleFileClick(file)}
                onDoubleClick={() => !file.isFolder && openExcelFile(file.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleFileClick(file);
                  }
                }}
                className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-neutral-100 ${
                  selectedFile?.id === file.id ? 'bg-success-50 border border-success-200' : ''
                }`}
              >
                {file.isFolder ? (
                  <svg className="w-8 h-8 text-warning-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-success-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zM8 11h2v2H8v-2zm0 3h2v2H8v-2zm0 3h2v2H8v-2zm3-6h5v2h-5v-2zm0 3h5v2h-5v-2zm0 3h5v2h-5v-2z"/>
                  </svg>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">{file.name}</p>
                  <p className="text-xs text-neutral-500">
                    {file.modifiedAt && new Date(file.modifiedAt).toLocaleDateString()}
                    {file.size && ` • ${formatFileSize(file.size)}`}
                  </p>
                </div>
                {!file.isFolder && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openExcelFile(file.id, 'web');
                      }}
                      className="p-1.5 text-neutral-400 hover:text-success-600"
                      title="Open in Excel Online"
                      aria-label="Open in Excel Online"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openExcelFile(file.id, 'desktop');
                      }}
                      className="p-1.5 text-neutral-400 hover:text-success-600"
                      title="Open in Desktop Excel"
                      aria-label="Open in Desktop Excel"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelWidgetRenderer;
